import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Math from "@/components/Math";
import { ArrowLeft, ArrowRight, BookOpen, Target, Sparkles } from "lucide-react";

export default function LearnUnit() {
  const { unitId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/learn/units/${unitId}`).then((r) => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [unitId]);

  if (loading) return <div className="text-zinc-500 text-sm" data-testid="learn-unit-loading">Loading unit…</div>;
  if (!data) return <div className="text-red-300 text-sm">Unit not found.</div>;

  const { course, unit, lessons, skills } = data;
  const practiceReady = unit.practice_bank_ready !== false;

  return (
    <div data-testid="learn-unit-page">
      <Link
        to={`/learn/${course.id}`}
        className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-yellow-300 mb-4"
        data-testid="learn-back-course"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {course.title}
      </Link>

      <div className="mb-6 fade-up">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300">
            Unit {unit.number} · {course.title.replace(/^Class \d+ · /, "Class " + course.grade)}
          </div>
          {unit.unit_group && (
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 border border-white/10 rounded-full px-2 py-0.5">
              {unit.unit_group}
            </div>
          )}
        </div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">{unit.title}</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl text-sm">{unit.summary}</p>

        {unit.formative_only_notes?.length > 0 && (
          <div className="mt-4 rounded-lg border border-yellow-300/25 bg-yellow-300/5 p-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-yellow-300 mb-1.5 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Formative-only for boards
            </div>
            <ul className="text-xs text-zinc-300 space-y-0.5 pl-4 list-disc marker:text-yellow-300/60">
              {unit.formative_only_notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
            <div className="text-[10px] text-zinc-500 mt-1.5">Learn these concepts — they won't appear in the CBSE 2026-27 board paper.</div>
          </div>
        )}
      </div>

      {/* Lessons ladder */}
      <section className="mb-8" data-testid="learn-unit-lessons">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-4 h-4 text-yellow-300" />
          <div className="font-display text-sm uppercase tracking-[0.2em] text-zinc-300">Lessons</div>
          <div className="text-[10px] font-mono text-zinc-500">{lessons.length} concept{lessons.length === 1 ? "" : "s"}</div>
        </div>
        {lessons.length === 0 ? (
          <div className="text-xs text-zinc-500 border border-white/5 rounded-lg p-4">No lessons in this unit yet.</div>
        ) : (
          <ol className="space-y-2">
            {lessons.map((l, i) => (
              <li key={l.id}>
                <Link
                  to={`/learn/lesson/${l.id}`}
                  data-testid={`learn-lesson-${l.id}`}
                  className="fade-up group flex items-start gap-3 rounded-lg border border-white/10 bg-[#152040] p-4 hover:border-yellow-300/40 transition-colors"
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <div className="w-8 h-8 rounded-full bg-white/5 grid place-items-center shrink-0 text-xs font-mono text-yellow-300">
                    {i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-sm font-semibold text-zinc-100 mb-0.5">{l.title}</div>
                    {l.body && (
                      <div className="text-xs text-zinc-500 line-clamp-2">
                        <Math>{l.body.slice(0, 220) + (l.body.length > 220 ? "…" : "")}</Math>
                      </div>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-500 group-hover:text-yellow-300 shrink-0 mt-1" />
                </Link>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Skills / Practice */}
      <section data-testid="learn-unit-skills">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-pink-300" />
          <div className="font-display text-sm uppercase tracking-[0.2em] text-zinc-300">Skills to practise</div>
        </div>
        {skills.length === 0 ? (
          <div className="text-xs text-zinc-500 border border-white/5 rounded-lg p-4">No skills configured yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {skills.map((s) => (
              <Link
                key={s.id}
                to={`/learn/skill/${s.id}`}
                data-testid={`learn-skill-${s.id}`}
                className="fade-up lift rounded-lg border border-white/10 bg-gradient-to-br from-pink-300/5 to-transparent p-4 flex items-center justify-between hover:border-pink-300/40 transition-colors"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-pink-300 mb-0.5">
                    Skill · {s.difficulty}
                  </div>
                  <div className="font-display text-sm text-zinc-100">{s.title}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-pink-300" />
              </Link>
            ))}
          </div>
        )}

        {!practiceReady && (
          <div className="mt-4 text-[11px] text-orange-300 bg-orange-300/5 border border-orange-300/20 rounded-lg p-3">
            Practice bank for this unit is in progress. You can still read the lessons above.
          </div>
        )}
      </section>
    </div>
  );
}
